import {Button as AntdButton, Col, Divider, Popconfirm, Row, Steps, theme} from 'antd'
import React, {useEffect, useState} from 'react'
import {Outlet, useLocation, useNavigate, useRouteLoaderData} from 'react-router-dom'
import {Project} from '../../entities/project'
import {useIntl} from 'react-intl'
import './EditProject.less'
import {useGardenPlanVariantParam} from '../../hooks/useGardenPlanVariantParam'

const routeSteps = [
  {route: '/edit', step: 0},
  {route: '/edit/plot', step: 1},
  {route: '/edit/buildings', step: 2},
  {route: '/edit/hardscape', step: 3},
  {route: '/edit/sectors', step: 4},
  {route: '/edit/waterscape', step: 5},
  {route: '/edit/solitarius', step: 6},
  {route: '/edit/other', step: 7},
]

export function calculateStatus(currentStep, step) {
  if (currentStep === step) {
    return 'process'
  } else if (currentStep > step) {
    return 'finish'
  } else {
    return 'wait'
  }
}

export type EditProjectProps = {
  type: 'new' | 'edit'
}

export const EditProjectContext = React.createContext<{
  setNextStepConfirmation: (confirmation: {
    message: string | null
    onConfirm: () => Promise<void>
  }) => void
}>({setNextStepConfirmation: () => {}})

export function EditProject({type}: EditProjectProps) {
  const {
    token: {controlHeightSM},
  } = theme.useToken()

  const navigate = useNavigate()
  const location = useLocation()
  const {withGardenPlanVariant} = useGardenPlanVariantParam()

  let project = null

  if (type === 'edit') {
    const data = useRouteLoaderData('project') as {project: Project}
    project = data.project
  }

  let currentStep = 0
  for (const {route, step} of routeSteps) {
    if (location.pathname.endsWith(route)) {
      currentStep = step
      break
    }
  }

  const onStepChange = (value: number) => {
    for (const {route, step} of routeSteps) {
      if (value === step) {
        navigate(withGardenPlanVariant(`/projects/${project?.id}${route}`))
        return
      }
    }
  }

  const intl = useIntl()

  const [nextStepConfirmation, setNextStepConfirmation] = useState<{
    message: string
    onConfirm: () => Promise<void>
  } | null>(null)

  const [nextStepConfirmOpen, setNextStepConfirmOpen] = useState(false)

  const setConfirmOpen = (nextOpen: boolean) => {
    if (!nextOpen) {
      setNextStepConfirmOpen(false)
    }

    if (!nextStepConfirmation) {
      onNextStepClick()
    } else {
      setNextStepConfirmOpen(true)
    }
  }

  useEffect(() => {
    setNextStepConfirmation(null)
    setNextStepConfirmOpen(false)
  }, [currentStep])

  const isLastStep = currentStep === routeSteps[routeSteps.length - 1].step
  const onNextStepClick = () => {
    if (isLastStep) {
      navigate(withGardenPlanVariant(`/projects/${project?.id}`))
    } else {
      onStepChange(currentStep + 1)
    }
  }

  return (
    <div>
      <Row>
        <div>
          <h2 style={{display: 'inline'}}>{project?.name}</h2>
        </div>
        <Divider style={{height: 'auto'}}></Divider>
      </Row>

      <Row>
        <div style={{display: 'flex', flexDirection: 'column', width: '100%'}}>
          <Steps
            type="navigation"
            current={currentStep}
            onChange={onStepChange}
            className="site-navigation-steps"
            items={[
              {
                title: intl.formatMessage({id: 'step'}) + ' 1',
                status: calculateStatus(currentStep, 0), // currentStep === 0 ? 'process' : 'finish',
                description: intl.formatMessage({id: 'editStepDefGenInfo'}),
              },
              {
                title: intl.formatMessage({id: 'step'}) + ' 2',
                status: calculateStatus(currentStep, 1),
                description: intl.formatMessage({id: 'editStepDefPlot'}),
                disabled: currentStep < 1 && type === 'new' ? true : false,
              },
              {
                title: intl.formatMessage({id: 'step'}) + ' 3',
                status: calculateStatus(currentStep, 2),
                description: intl.formatMessage({id: 'editStepDefBuildings'}),
                disabled: currentStep < 2 && type === 'new' ? true : false,
              },
              {
                title: intl.formatMessage({id: 'step'}) + ' 4',
                status: calculateStatus(currentStep, 3),
                description: intl.formatMessage({id: 'editStepDefHardscape'}),
                disabled: currentStep < 3 && type === 'new' ? true : false,
              },
              {
                title: intl.formatMessage({id: 'step'}) + ' 5',
                status: calculateStatus(currentStep, 4),
                description: intl.formatMessage({id: 'editStepDefSoftscape'}),
                disabled: currentStep < 4 && type === 'new' ? true : false,
              },
              {
                title: intl.formatMessage({id: 'step'}) + ' 6',
                status: calculateStatus(currentStep, 5),
                description: intl.formatMessage({id: 'editStepDefWaterscape'}),
                disabled: currentStep < 5 && type === 'new' ? true : false,
              },
              {
                title: intl.formatMessage({id: 'step'}) + ' 7',
                status: calculateStatus(currentStep, 6),
                description: intl.formatMessage({id: 'editStepDefSolitarius'}),
                disabled: currentStep < 6 && type === 'new' ? true : false,
              },
              {
                title: intl.formatMessage({id: 'step'}) + ' 8',
                status: calculateStatus(currentStep, 7),
                description: intl.formatMessage({id: 'editStepDefOtherElements'}),
                disabled: currentStep < 7 && type === 'new' ? true : false,
              },
            ]}
          />
          <Divider style={{height: 'auto'}}></Divider>
        </div>
      </Row>
      <Row>
        <Col style={{width: '100%'}}>
          <div style={{backgroundColor: '#fff7e6', padding: '20px', position: 'relative'}}>
            <div>
              <div style={{position: 'absolute', height: 50}} />
              <h2 style={{display: 'inline'}}>
                {intl.formatMessage({id: 'step'})} {currentStep + 1}
              </h2>

              <Popconfirm
                open={nextStepConfirmOpen}
                onOpenChange={setConfirmOpen}
                title={intl.formatMessage({id: 'projectIsNotSaved'})}
                overlayStyle={{maxWidth: 700}}
                description={
                  <div>
                    <div>{nextStepConfirmation?.message}</div>
                    <div style={{marginBottom: '-40px', marginTop: '10px'}}>
                      <AntdButton onClick={() => setNextStepConfirmOpen(false)}>
                        {intl.formatMessage({id: 'cancel'})}
                      </AntdButton>
                      <AntdButton
                        style={{marginLeft: '10px'}}
                        onClick={() => {
                          setNextStepConfirmOpen(false)
                          onNextStepClick()
                        }}
                      >
                        {intl.formatMessage({id: 'continueWithoutSaving'})}
                      </AntdButton>
                    </div>
                  </div>
                }
                showCancel={false}
                onConfirm={async () => {
                  await nextStepConfirmation?.onConfirm?.()
                  onNextStepClick()
                }}
                okText={intl.formatMessage({id: 'saveAndContinue'})}
                okButtonProps={{size: 'middle'}}
              >
                <AntdButton style={{position: 'absolute', right: '15px'}} type="primary">
                  {isLastStep
                    ? intl.formatMessage({id: 'projectPage'})
                    : intl.formatMessage({id: 'next'})}
                </AntdButton>
              </Popconfirm>
            </div>

            <EditProjectContext.Provider value={{setNextStepConfirmation}}>
              <Outlet />
            </EditProjectContext.Provider>
          </div>
        </Col>
      </Row>
    </div>
  )
}
